// 全站共用的連線狀態——單一來源，離線提示（api.js）與 autosave 佇列的
// 「線上／離線／待同步」徽章（Budget/edit/useEstimateSave.js）都讀這裡，
// 不要各自監聽 navigator.onLine／'online'/'offline' 兩套。
//
// 用法：
//   import { networkState } from '@/Common/network.js'
//   if (!networkState.online) { ... }        // 讀
//   <span v-if="!networkState.online">離線</span>  // 直接綁 template（reactive）
//
// ⚠⚠⚠ 2026-09-30 起 `online` 不再只反映 `navigator.onLine`——使用者截圖回報
// 「Wi-Fi 還在、站台連不到，登入頁卻看起來跟平常一樣」，根因是 `navigator.
// onLine` 在多數瀏覽器只代表「網卡有沒有連上網路」，站台本身掛了或連不到
// 不會讓這個值變 false。現在 `online` = `networkCardOnline`（原來的
// `navigator.onLine`）**且**站台探測可達（`serverReachable`，靠新端點
// `GET /Login/Ping` 探測）——判斷邏輯本身抽成零 import 的純函式放在
// `networkStateLogic.js`（方便 tests/unit 直接測），這裡只負責把探測結果
// 寫進 Vue 的 reactive 狀態。`serverReachable`／`networkCardOnline` 個別
// 保留給需要區分「網路卡沒連」跟「連上網路但站台連不到」的地方用
// （目前只有 NetworkStatusWidget.vue 的三態徽章、Login.vue 的離線原因文字）。
import { reactive } from 'vue'
import { resolveOnline } from './networkStateLogic.js'

const PING_TIMEOUT_MS = 3_000
const PING_INTERVAL_MS = 30_000

function pingUrl() {
  const base = (typeof window !== 'undefined' && window.__APP_BASE__) || '/'
  return base.replace(/\/+$/, '') + '/Login/Ping'
}

const state = reactive({
  networkCardOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  // 樂觀初始值：頁面剛載入、第一次探測還沒回來之前先假設站台可達，避免每次
  // 開頁都先閃一次「離線」畫面——`checkServerReachable()` 的結果回來後才會
  // 真的校正（多數時候會在使用者注意到之前就完成，因為逾時只有 3 秒）。
  serverReachable: true,
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
})

/** 離線時在 <html> 掛 is-offline（2026-10-05，1151004 測試第 12 條「沒有網路情況下，背景改成粉色」）——
 *  樣式在 assets/main.css。掛在這裡是因為這支是連線狀態的唯一來源，全站每一頁（含登入頁）都會載入。 */
function applyOfflineClass() {
  // 單元測試等非瀏覽器環境可能只有半套 document，拿不到 <html> 就不做
  if (typeof document === 'undefined' || !document.documentElement) return
  document.documentElement.classList.toggle('is-offline', !state.online)
}

function recompute() {
  const wasOnline = state.online
  state.online = resolveOnline(state.networkCardOnline, state.serverReachable)
  applyOfflineClass()
  // Wi-Fi 全程沒斷、只是站台一度連不到又恢復的情境——原生 'online' 事件不會
  // 再發一次（網卡狀態根本沒變過），這裡補發一個自訂事件，`api.js` 的
  // `requestOnlineLogin()` 監聽它（見該檔），離線登入的使用者才不會在這種
  // 情境下永遠等不到「連線恢復，請重新登入」的提示。
  if (!wasOnline && state.online && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('pcces-online-reachable'))
  }
}

let pingInFlight = null
/**
 * 探測站台是否可達：`GET /Login/Ping`，預期 204。
 *
 * ⚠⚠ 刻意不走 `myAjax`——純粹的可達性探測不需要全域 loading 遮罩、也不需要
 * 帶 anti-forgery header（`/Login/Ping` 本身匿名可存取、不改任何資料，見
 * `LoginController.Ping` 與 `ControllerGuardTests.AllowAnonymous` 的理由），
 * 用 `fetch` 自己控制逾時——3 秒是刻意設得比 `myAjax` 預設的 60 秒短很多，
 * 這只是「有沒有回應」的探測，沒必要讓使用者等到一般 API 逾時的長度。
 *
 * 已知 SW（`pwa/sw-template.js`）的 `fetch` 事件處理器不會攔截這個請求：
 * 這裡不是 `navigate` 模式（`fetch()` 呼叫，不是頁面導航），路徑也不在
 * `isChunkRequest`／`isShellAssetRequest` 涵蓋的資源清單裡，SW 的
 * `fetch` handler 三個分支都不成立，事件直接放行給瀏覽器打真正的網路。
 */
export async function checkServerReachable() {
  if (typeof fetch !== 'function') return state.serverReachable
  if (pingInFlight) return pingInFlight
  pingInFlight = (async () => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS)
    try {
      const res = await fetch(pingUrl(), {
        method: 'GET', cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
      })
      state.serverReachable = !!res.ok
    } catch {
      state.serverReachable = false
    } finally {
      clearTimeout(timer)
      recompute()
    }
    return state.serverReachable
  })()
  try {
    return await pingInFlight
  } finally {
    pingInFlight = null
  }
}

/** `myAjax` 任何成功回應（含伺服器回的業務錯誤，只要 HTTP 層真的有回應）都
 * 呼叫這個——一次成功的 API 呼叫本身就是「站台可達」最直接的證據，比等下一次
 * 週期性探測更即時。見 `Common/api.js` 攔截器。 */
export function markServerReachable() {
  state.serverReachable = true
  recompute()
}

/** `myAjax` 遇到網路錯誤／逾時（沒有收到任何 HTTP 回應）呼叫這個——立即標記
 * 不可達，並馬上重測一次（不等下一個 30 秒週期），站台如果其實是好的、只是
 * 剛好那一次請求不順，很快就會被這次重測糾正回來。見 `Common/api.js` 攔截器。 */
export function markServerUnreachable() {
  state.serverReachable = false
  recompute()
  checkServerReachable()
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    state.networkCardOnline = true
    recompute()
    checkServerReachable()
  })
  window.addEventListener('offline', () => { state.networkCardOnline = false; recompute() })
  applyOfflineClass()
  checkServerReachable()
  setInterval(checkServerReachable, PING_INTERVAL_MS)
}

export const networkState = state
